import { useCallback, useSyncExternalStore } from 'react';
import { useOneTrustContext } from '@app/onetrust';
import { READ_ITEMS_STORAGE_KEY } from '../constants';
import {
  addReadEntry,
  getReadIdsSnapshot,
  isWithinUnreadWindow,
} from '../storage/localStorage';

// ---------------------------------------------------------------------------
// Subscription — wires useSyncExternalStore to localStorage changes.
//
// Two event sources are needed:
//   - 'storage': fired by the browser on all OTHER tabs when localStorage
//     changes. Gives us cross-tab sync for free. We filter by key so that
//     unrelated cross-tab writes (any other localStorage key) don't trigger
//     re-renders in every component using this hook.
//   - 'unread-indicator-changed': a custom event dispatched by addReadEntry()
//     after it writes. The browser does NOT fire 'storage' for same-window
//     writes, so we emit our own event to keep same-tab components in sync.
// ---------------------------------------------------------------------------
const subscribe = (onStoreChange: () => void): (() => void) => {
  // Filter the cross-tab 'storage' event to only fire for our key. Note that
  // event.key is null when localStorage.clear() is called — we treat that as
  // a relevant change too, since it wipes our read set.
  const handleStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === READ_ITEMS_STORAGE_KEY) {
      onStoreChange();
    }
  };

  window.addEventListener('storage', handleStorage);
  window.addEventListener('unread-indicator-changed', onStoreChange);

  return () => {
    window.removeEventListener('storage', handleStorage);
    window.removeEventListener('unread-indicator-changed', onStoreChange);
  };
};

interface UseUnreadStateInput {
  /** The post's unique id — used to look up read state in localStorage. */
  id: string;
  /** The post's first publish timestamp as an ISO 8601 string from the backend. */
  firstPublishedAt: string;
}

interface UseUnreadStateResult {
  /**
   * True when the post has not been marked seen
   *  AND is within the 7-day unread window
   *  AND the user has not opted out of analytics.
   */
  isUnread: boolean;
  /**
   * Write this Change's id to the read set. No-op when analyticsOptOut is true.
   */
  markSeen: () => void;
}

/**
 * Per-row hook that tracks whether a single Change is unread.
 */
export const useUnreadState = ({
  id,
  firstPublishedAt,
}: UseUnreadStateInput): UseUnreadStateResult => {
  const { analyticsOptOut } = useOneTrustContext();

  // Subscribe to the external read-set store.
  const readIds = useSyncExternalStore(subscribe, getReadIdsSnapshot);

  const isUnread =
    !analyticsOptOut &&
    isWithinUnreadWindow(firstPublishedAt) &&
    !readIds.includes(id);

  const markSeen = useCallback(() => {
    if (analyticsOptOut) {
      return;
    }

    addReadEntry({ id, firstPublishedAt });
  }, [analyticsOptOut, id, firstPublishedAt]);

  return { isUnread, markSeen };
};
